import React, { useCallback, useEffect, useState } from "react";
import { loadOnlineUsers, filterUsers, loadUsers } from "../../../../utils/Network";
import { userAvatar } from "../../../../utils";
import TabsWidget from "./TabsWidget";

// ─── constants ────────────────────────────────────────────────────────────────
const ONLINE_MINS   = 10;      // "online now" window (minutes)
const ONLINE_LIST_MIN = 10;    // show at least this many users (recent activity fills past the window)
const ACTIVE_MINS   = 1440;    // "active today" window (24 h)
const POLL_INTERVAL = 60_000;  // auto-refresh every 60 s

const TABS = [
  { key: "new",    label: "New"    },
  { key: "online", label: "Online" },
  { key: "total",  label: "Total"  },
];

// ─── helpers ──────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#7b4f4f", "#7b6540", "#4f7b55", "#3d6b72",
  "#3d5472", "#5c4f7b", "#724f6b", "#4f6b5c",
  "#72604f", "#4f6372", "#6b724f", "#5e4f72",
];

function avatarColor(user) {
  const seed = user.id ?? 0;
  return AVATAR_COLORS[seed % AVATAR_COLORS.length];
}

function minutesAgo(dateStr) {
  if (!dateStr) return Infinity;
  return (Date.now() - new Date(dateStr).getTime()) / 60_000;
}

/** Best timestamp for "last seen" — matches backend Coalesce(last_online, last_login). */
function lastActivityAt(user) {
  return user.last_online || user.last_login || null;
}

function isOnlineNow(user) {
  const t = lastActivityAt(user);
  if (!t) return false;
  return minutesAgo(t) < ONLINE_MINS;
}

function timeAgoLabel(dateStr) {
  if (!dateStr) return null;
  const secs = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (secs < 10)  return "now";
  if (secs < 60)  return `${secs} second${secs !== 1 ? "s" : ""} ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60)  return `${mins} minute${mins !== 1 ? "s" : ""} ago`;
  const hrs  = Math.floor(mins / 60);
  if (hrs  < 24)  return `${hrs} hour${hrs !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

// ─── sub-components ───────────────────────────────────────────────────────────
function UserRow({ user }) {
  const [imageError, setImageError] = useState(false);
  const activity = lastActivityAt(user);
  const isOnline = isOnlineNow(user);
  const label    = timeAgoLabel(activity);
  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
  const name     = `${capitalize(user.first_name)} ${capitalize(user.last_name)}`.trim() || "—";
  const avatarSrc = userAvatar(user);
  const isDefaultAvatar =
    !avatarSrc ||
    avatarSrc.includes("/assets/dist/img/avatar-f.jpeg") ||
    avatarSrc.includes("/assets/dist/img/avatar-m.jpeg") ||
    avatarSrc.includes("/assets/images/default.png");
  const initial = (user.first_name || user.last_name || "?").trim().charAt(0).toUpperCase();

  return (
    <li className="ou-row">
      <a href={`/cms/usertalent/${user.id}`} className="ou-user-link">
        <div className="ou-avatar-wrap">
          {!imageError && avatarSrc && !isDefaultAvatar ? (
            <img
              src={avatarSrc}
              alt={name}
              className="ou-avatar"
              onError={() => setImageError(true)}
            />
          ) : (
            <div
              className="ou-avatar ou-avatar-fallback"
              aria-label={name}
              style={{ backgroundColor: avatarColor(user) }}
            >
              {initial}
            </div>
          )}
          {isOnline && <span className="ou-pulse" />}
        </div>
        <span className="ou-name kfont">{name}</span>
      </a>
      {label && (
        <span className="ou-time-badge">
          <i className="fa fa-clock-o" /> {label}
        </span>
      )}
    </li>
  );
}

function UserList({ users, loading }) {
  if (loading) {
    return (
      <div className="ou-state-msg">
        <i className="fa fa-spinner fa-spin" /> Loading…
      </div>
    );
  }
  if (!users.length) {
    return <div className="ou-state-msg">No users to show</div>;
  }
  return (
    <ul className="ou-list">
      {users.map((u) => (
        <UserRow key={u.id} user={u} />
      ))}
    </ul>
  );
}

function TotalStats({ totalCount, onlineCount, activeCount }) {
  const stats = [
    { label: "Online now",       value: onlineCount,       dot: "green"  },
    { label: "Active today",     value: activeCount,       dot: "yellow" },
    { label: "Total registered", value: totalCount ?? "—", dot: "gray"   },
  ];
  return (
    <div className="ou-total-stats">
      {stats.map(({ label, value, dot }) => (
        <div key={label} className="ou-stat-row">
          <span className={`ou-stat-dot ou-dot-${dot}`} />
          <span className="ou-stat-label">{label}</span>
          <span className="ou-stat-value">
            {typeof value === "number" ? value.toLocaleString() : value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── main widget ──────────────────────────────────────────────────────────────
export default function WidgetOnlineUsers() {
  const [activeTab, setActiveTab] = useState("new");

  // Each tab has its own data slice
  const [onlineUsers, setOnlineUsers]   = useState([]);
  const [newUsers,    setNewUsers]       = useState([]);
  const [totalCount,  setTotalCount]     = useState(null);
  const [activeCount, setActiveCount]    = useState(null);

  const [loadingOnline, setLoadingOnline] = useState(true);
  const [loadingNew,    setLoadingNew]    = useState(true);

  // ── fetchers (each tab calls the right endpoint) ──────────────────────────

  const fetchOnline = useCallback(() => {
    setLoadingOnline(true);
    // Dedicated backend view: only users active in the last 5 min
    loadOnlineUsers(ONLINE_MINS, { minCount: ONLINE_LIST_MIN }).then((data) => {
      setOnlineUsers(data?.results ?? []);
      setLoadingOnline(false);
    });
  }, []);

  const fetchNew = useCallback(() => {
    setLoadingNew(true);
    // Newest registered users (backend returns ordered by -id via UserListAPIView default)
    loadUsers(1, 10).then((data) => {
      setNewUsers(data?.results ?? []);
      setTotalCount(data?.count ?? null);
      setLoadingNew(false);
    });
  }, []);

  const fetchActiveCount = useCallback(() => {
    // Users active in last 24 h — used for the "Total" tab stats only
    loadOnlineUsers(ACTIVE_MINS).then((data) => {
      setActiveCount(data?.count ?? null);
    });
  }, []);

  useEffect(() => {
    fetchOnline();
    fetchNew();
    fetchActiveCount();

    const id = setInterval(() => {
      fetchOnline();
      // New/active count refresh less aggressively — every 5 min
    }, POLL_INTERVAL);

    const idSlow = setInterval(() => {
      fetchNew();
      fetchActiveCount();
    }, 5 * POLL_INTERVAL);

    return () => {
      clearInterval(id);
      clearInterval(idSlow);
    };
  }, [fetchOnline, fetchNew, fetchActiveCount]);

  const tabLoading = {
    online: loadingOnline,
    new:    loadingNew,
  };

  const tabUsers = {
    online: onlineUsers,
    new:    newUsers,
  };

  return (
    <div className="box box-solid bg-dark widget-online-users">
      

      <div className="box-body no-padding">
        <TabsWidget
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          className="cms-page-tabs"
        />

        {activeTab === "total" ? (
          <TotalStats
            totalCount={totalCount}
            onlineCount={onlineUsers.filter(isOnlineNow).length}
            activeCount={activeCount}
          />
        ) : (
          <UserList
            users={tabUsers[activeTab] ?? []}
            loading={tabLoading[activeTab]}
          />
        )}
      </div>
    </div>
  );
}
